import { useQuery } from '@tanstack/react-query';
import { AuditPageSchema, type AuditPage } from './platformAudit';
import { useTenantContext } from '../auth/useTenantContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

// ─── Filters + query-string builder ──────────────────────────────────────────

export interface TenantAuditFilters {
  from?: string; to?: string;
  actor_user_ids?: number[];
  actions?: string[]; entity_types?: string[];
  entity_id?: string; payload_search?: string;
  cursor?: string; limit?: number;
}

export function buildTenantAuditQueryString(f: TenantAuditFilters): string {
  const qs = new URLSearchParams();
  if (f.from) qs.set('from', f.from);
  if (f.to)   qs.set('to', f.to);
  for (const id of f.actor_user_ids ?? []) qs.append('actor_user_ids[]', String(id));
  for (const a  of f.actions ?? [])        qs.append('actions[]', a);
  for (const e  of f.entity_types ?? [])   qs.append('entity_types[]', e);
  if (f.entity_id)      qs.set('entity_id', f.entity_id);
  if (f.payload_search) qs.set('payload_search', f.payload_search);
  if (f.cursor) qs.set('cursor', f.cursor);
  if (f.limit)  qs.set('limit', String(f.limit));
  return qs.toString();
}

// ─── Internal fetch helper ────────────────────────────────────────────────────

async function authedFetch(
  path: string,
  token: string,
  tenantCode: string,
  warehouseId: number | null = null,
): Promise<unknown> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

// ─── Query hook ───────────────────────────────────────────────────────────────

export function useTenantAuditLog(
  token: string | null,
  tenantCode: string | null,
  filters: TenantAuditFilters,
) {
  const { warehouseId } = useTenantContext();
  const qs = buildTenantAuditQueryString(filters);
  return useQuery<AuditPage>({
    queryKey: ['admin', 'audit', 'tenant', tenantCode, filters],
    queryFn: async () => AuditPageSchema.parse(await authedFetch(`/admin/audit?${qs}`, token!, tenantCode!, warehouseId)),
    enabled: token !== null && tenantCode !== null,
  });
}

// ─── CSV download helper ──────────────────────────────────────────────────────

export async function downloadTenantAuditCsv(
  token: string,
  tenantCode: string,
  filters: TenantAuditFilters,
  warehouseId: number | null = null,
): Promise<void> {
  const qs = buildTenantAuditQueryString(filters);
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);
  const res = await fetch(`${API_BASE}/admin/audit?${qs}&format=csv`, { headers });
  if (!res.ok) throw new Error(`CSV export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
